import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from '../api/axios';
import { Box, Typography, Table, TableBody, TableCell, TableHead, TableRow, Paper } from '@mui/material';

const EstimatePrintView = () => {
  const { id } = useParams();
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchEstimate = useCallback(async () => {
    try {
      const response = await axios.get(`/api/estimates/${id}`);
      setEstimate(response.data.estimate);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchEstimate();
  }, [fetchEstimate]);

  useEffect(() => {
    // Auto-print when loaded
    if (estimate && !loading) {
      setTimeout(() => {
        window.print();
      }, 500);
    }
  }, [estimate, loading]);

  if (loading || !estimate) {
    return <Box p={4}><Typography>Loading...</Typography></Box>;
  }

  const getSubtotal = () => {
    if (!estimate || !estimate.rooms) return 0;
    return estimate.rooms.reduce((total, room) => {
      const roomTotal = room.items.reduce((sum, item) => sum + parseFloat(item.cost || 0), 0);
      return total + roomTotal;
    }, 0);
  };

  const getTaxAmount = () => {
    const subtotal = getSubtotal();
    const taxRate = parseFloat(estimate?.tax_rate || 0);
    return (subtotal * taxRate) / 100;
  };

  const getTotalCost = () => {
    return getSubtotal() + getTaxAmount();
  };

  const getCostByType = () => {
    if (!estimate || !estimate.rooms) return { labor: 0, materials: 0, both: 0 };
    return estimate.rooms.reduce((totals, room) => {
      room.items.forEach(item => {
        const cost = parseFloat(item.cost || 0);
        totals[item.type] = (totals[item.type] || 0) + cost;
      });
      return totals;
    }, { labor: 0, materials: 0, both: 0 });
  };

  const costBreakdown = getCostByType();

  return (
    <Box sx={{
      p: 4,
      maxWidth: '800px',
      margin: '0 auto',
      '@media print': {
        p: 2
      }
    }}>
      <style>
        {`
          @media print {
            @page { margin: 1cm; }
            body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
          }
        `}
      </style>

      {/* Header */}
      <Box textAlign="center" mb={4}>
        <Typography variant="h3" gutterBottom fontWeight="bold">
          PROJECT ESTIMATE
        </Typography>
      </Box>

      {/* From/To Section */}
      <Box display="flex" justifyContent="space-between" mb={4}>
        <Box>
          <Typography variant="h6" fontWeight="bold" gutterBottom>FROM:</Typography>
          {estimate.contractor_business_name && (
            <Typography>{estimate.contractor_business_name}</Typography>
          )}
          <Typography>{estimate.contractor_first_name} {estimate.contractor_last_name}</Typography>
          {estimate.contractor_business_address && (
            <>
              <Typography>{estimate.contractor_business_address}</Typography>
              <Typography>
                {estimate.contractor_business_city}, {estimate.contractor_business_state} {estimate.contractor_business_zip}
              </Typography>
            </>
          )}
          <Typography>Email: {estimate.contractor_email}</Typography>
          {estimate.contractor_phone && <Typography>Phone: {estimate.contractor_phone}</Typography>}
        </Box>
        <Box>
          <Typography variant="h6" fontWeight="bold" gutterBottom>TO:</Typography>
          <Typography>{estimate.homeowner_first_name} {estimate.homeowner_last_name}</Typography>
          {estimate.homeowner_home_address && (
            <>
              <Typography>{estimate.homeowner_home_address}</Typography>
              <Typography>
                {estimate.homeowner_home_city}, {estimate.homeowner_home_state} {estimate.homeowner_home_zip}
              </Typography>
            </>
          )}
          <Typography>Email: {estimate.homeowner_email}</Typography>
        </Box>
      </Box>

      {/* Estimate Details */}
      <Paper sx={{ p: 2, mb: 3, backgroundColor: '#f5f5f5' }}>
        <Typography variant="h6" gutterBottom>ESTIMATE DETAILS</Typography>
        <Typography>Project Name: {estimate.name}</Typography>
        <Typography>Estimate Date: {new Date(estimate.created_at).toLocaleDateString()}</Typography>
        {estimate.submitted_at && (
          <Typography>Submitted: {new Date(estimate.submitted_at).toLocaleDateString()}</Typography>
        )}
        <Typography>Status: {estimate.status.toUpperCase()}</Typography>
      </Paper>

      {/* Items by Room */}
      <Typography variant="h5" gutterBottom fontWeight="bold" mt={4}>
        ITEMIZED BREAKDOWN
      </Typography>

      {estimate.rooms && estimate.rooms.map((room) => (
        <Box key={room.id} mb={4}>
          <Typography variant="h6" color="primary" gutterBottom fontWeight="bold">
            {room.name}
          </Typography>

          {room.items && room.items.length > 0 ? (
            <>
              <Table size="small" sx={{ mb: 2 }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#e3f2fd' }}>
                    <TableCell><strong>Item</strong></TableCell>
                    <TableCell><strong>Type</strong></TableCell>
                    <TableCell align="right"><strong>Cost</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {room.items.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>{item.title}</TableCell>
                      <TableCell sx={{ textTransform: 'capitalize' }}>{item.type}</TableCell>
                      <TableCell align="right">${parseFloat(item.cost).toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={2} align="right"><strong>Room Subtotal:</strong></TableCell>
                    <TableCell align="right">
                      <strong>
                        ${room.items.reduce((sum, item) => sum + parseFloat(item.cost || 0), 0).toFixed(2)}
                      </strong>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 2 }}>
              No items in this room
            </Typography>
          )}
        </Box>
      ))}

      {/* Cost Summary */}
      <Box mt={6} pt={3} borderTop={2} borderColor="divider">
        <Typography variant="h5" gutterBottom fontWeight="bold">
          COST SUMMARY
        </Typography>
        <Box display="flex" flexDirection="column" alignItems="flex-end" gap={1}>
          {costBreakdown.labor > 0 && (
            <Typography variant="h6">Labor: ${costBreakdown.labor.toFixed(2)}</Typography>
          )}
          {costBreakdown.materials > 0 && (
            <Typography variant="h6">Materials: ${costBreakdown.materials.toFixed(2)}</Typography>
          )}
          {costBreakdown.both > 0 && (
            <Typography variant="h6">Labor & Materials: ${costBreakdown.both.toFixed(2)}</Typography>
          )}

          <Box mt={2} pt={2} borderTop={1} borderColor="divider" width="fit-content" minWidth={300}>
            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography variant="h6">Subtotal:</Typography>
              <Typography variant="h6" fontWeight="bold">
                ${getSubtotal().toFixed(2)}
              </Typography>
            </Box>
            {parseFloat(estimate?.tax_rate || 0) > 0 && (
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="h6">Tax ({parseFloat(estimate?.tax_rate || 0).toFixed(2)}%):</Typography>
                <Typography variant="h6" fontWeight="bold">
                  ${getTaxAmount().toFixed(2)}
                </Typography>
              </Box>
            )}
          </Box>

          <Box mt={2} p={2} bgcolor="success.light" borderRadius={1} width="fit-content" minWidth={300}>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="h4" fontWeight="bold" color="success.dark">
                TOTAL:
              </Typography>
              <Typography variant="h4" fontWeight="bold" color="success.dark">
                ${getTotalCost().toFixed(2)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Footer */}
      <Box mt={6} textAlign="center">
        <Typography variant="caption" color="text.secondary" fontStyle="italic">
          This estimate is valid for 30 days from the date above. Final costs may vary based on actual conditions and change orders.
        </Typography>
      </Box>
    </Box>
  );
};

export default EstimatePrintView;
